import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import eventsData from '../data/events.json';
import '../styles/Registration.css'; // EventDetail ki CSS

function Registration() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    teamName: '',
    captainName: '',
    email: '',
    phone: '',
    players: ['', '', '', '', ''],
    terms: false
  });

  useEffect(() => {
    const foundEvent = eventsData.events.find(e => e.id === parseInt(eventId));
    setEvent(foundEvent);
    setLoading(false);
  }, [eventId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePlayerChange = (index, value) => {
    const newPlayers = [...formData.players];
    newPlayers[index] = value;
    setFormData({ ...formData, players: newPlayers });
  };

  const addPlayer = () => {
    setFormData({
      ...formData,
      players: [...formData.players, '']
    });
  };

  const removePlayer = (index) => {
    if (formData.players.length <= 5) return;
    const newPlayers = formData.players.filter((_, i) => i !== index);
    setFormData({ ...formData, players: newPlayers });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const booking = {
      id: Date.now(),
      eventId: event.id,
      eventTitle: event.title,
      eventDate: event.date,
      ...formData,
      registeredAt: new Date().toISOString()
    };

    const existingBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    localStorage.setItem('bookings', JSON.stringify([...existingBookings, booking]));

    navigate('/confirmation', { state: { booking } });
  };

  if (loading) {
    return (
      <div className="registration-page-wrapper">
        <div className="reg-bg-glow reg-bg-glow-1"></div>
        <div className="reg-bg-glow reg-bg-glow-2"></div>
        <div className="reg-container">
          <div className="reg-loading">
            <div className="reg-loader"></div>
            <p>Loading registration form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="registration-page-wrapper">
        <div className="reg-bg-glow reg-bg-glow-1"></div>
        <div className="reg-bg-glow reg-bg-glow-2"></div>
        <div className="reg-container">
          <div className="reg-not-found">
            <h2>Event not found!</h2>
            <p>The event you're trying to register for doesn't exist.</p>
            <Link to="/events" className="reg-not-found-btn">← Browse Events</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-page-wrapper">
      {/* Animated Background Glows */}
      <div className="reg-bg-glow reg-bg-glow-1"></div>
      <div className="reg-bg-glow reg-bg-glow-2"></div>

      <div className="reg-container">
        {/* Registration Card */}
        <div className="reg-card">
          {/* Header */}
          <div className="reg-card-header">
            <span className="reg-card-icon">📝</span>
            <h1 className="reg-card-title">Team Registration</h1>
            <p className="reg-card-subtitle">Fill in the details to register your team</p>
            <div className="reg-event-info">
              <span className="reg-event-tag">
                🏆 <strong>{event.title}</strong>
              </span>
              <span className="reg-event-tag">
                📅 {event.date}
              </span>
              <span className="reg-event-tag">
                💰 {event.fee}
              </span>
            </div>
          </div>

          {/* Form */}
          <form className="reg-form" onSubmit={handleSubmit}>
            {/* Team Name */}
            <div className="reg-form-group">
              <label>
                Team Name <span className="reg-required">*</span>
              </label>
              <input
                type="text"
                name="teamName"
                value={formData.teamName}
                onChange={handleChange}
                required
                placeholder="Enter your team name"
              />
            </div>

            {/* Captain Name */}
            <div className="reg-form-group">
              <label>
                Captain Name <span className="reg-required">*</span>
              </label>
              <input
                type="text"
                name="captainName"
                value={formData.captainName}
                onChange={handleChange}
                required
                placeholder="Enter captain name"
              />
            </div>

            {/* Email & Phone */}
            <div className="reg-form-row">
              <div className="reg-form-group">
                <label>
                  Email <span className="reg-required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="captain@email.com"
                />
              </div>

              <div className="reg-form-group">
                <label>
                  Phone <span className="reg-required">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="9876543210"
                />
              </div>
            </div>

            {/* Players */}
            <div className="reg-form-group">
              <div className="reg-players-label">
                <label>
                  Team Players <span className="reg-required">*</span>
                </label>
                <span className="reg-players-count">
                  {formData.players.filter(p => p.trim()).length} players added
                </span>
              </div>
              {formData.players.map((player, index) => (
                <div key={index} className="reg-player-input">
                  <input
                    type="text"
                    placeholder={`Player ${index + 1}`}
                    value={player}
                    onChange={(e) => handlePlayerChange(index, e.target.value)}
                  />
                  {index >= 5 && (
                    <button type="button" className="reg-remove-btn" onClick={() => removePlayer(index)}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="reg-add-player-btn" onClick={addPlayer}>
                + Add Player
              </button>
            </div>

            {/* Terms */}
            <label className="reg-checkbox">
              <input
                type="checkbox"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                required
              />
              <span>
                I agree to the <strong style={{ color: '#ffd200' }}>terms and conditions</strong> <span className="reg-required">*</span>
              </span>
            </label>

            {/* Submit */}
            <button type="submit" className="reg-submit-btn">
              ✅ Register Team
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Registration;